import {share} from 'rxjs/operators';
/**
 * Created by djacobsmeyer on 2/16/17.
 */
import {Injectable} from '@angular/core';
import {NotificationService} from './NotificationService';
import {
    CommitEfnCaseParameters, DeclineEfnCaseParameters,
    EfnCase,
    EfnCaseFile,
    EfnClient,
    EfnCommentsClient,
    EfnFileClient,
    InviteAgencyParams,
    PagedListOfEfnCase
} from './api.client';
import {EfnCaseStatusType} from './api.client';

import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {AgencyEfnCase} from './api.client';
import {EfnCaseComment} from './api.client';
import {StateResetRegistry} from './state-reset-registry.service';

// 003-retire-ngrx (US2): the efn slice moved off NgRx into this service.
// Cases genuinely span views (dashboard summaries, EFN list, client tabs,
// edit page) so the slice stays shared. In US3, agencyEfnCases moved here
// from the retired agency slice — this service is its producer and only EFN
// views read it (the old selector was even named selectEfnDataAgencyEfnCases).
// The paged search results are NOT held here: their only consumer is
// EfnListComponent, whose search criteria live on the component —
// service-held results outlived the criteria that produced them, breaking
// pagination after back-navigation.
interface EfnSliceState {
    selectedEfnCase: EfnCase;
    clientsEfnCases: EfnCase[];
    openEfnCases: EfnCase[];
    fundedEfnCases: EfnCase[];
    agencyEfnCases: AgencyEfnCase[];
}

const initialState: EfnSliceState = {
    selectedEfnCase: null,
    clientsEfnCases: null,
    openEfnCases: null,
    fundedEfnCases: null,
    agencyEfnCases: []
};

@Injectable()
export class EfnService {

    private readonly _state$ = new BehaviorSubject<EfnSliceState>(initialState);

    // ---------------- Streams (former efn selectors) ----------------
    readonly selectedEfnCase$ = this.select((s) => s.selectedEfnCase);
    readonly clientsEfnCases$ = this.select((s) => s.clientsEfnCases);
    readonly openEfnCases$ = this.select((s) => s.openEfnCases);
    readonly fundedEfnCases$ = this.select((s) => s.fundedEfnCases);
    readonly agencyEfnCases$ = this.select((s) => s.agencyEfnCases);

    constructor(private _efnApiService: EfnClient,
                private _efnCommentApiService: EfnCommentsClient,
                private _notificationService: NotificationService,
                private _efnFileApiService: EfnFileClient,
                resetRegistry: StateResetRegistry) {
        resetRegistry.register(() => this._state$.next(initialState));
    }

    addEfnCase(newCase: EfnCase): Observable<EfnCase> {
        const request = this._efnApiService.addEfnCase(newCase).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setSelectedEfnCaseAction(result);
                this._notificationService.showSuccess('EFN Case successfully added!');
            }, error: () => {
                this._notificationService.showError('There was an error creating this EFN Case.');
            }
        });
        return request;
    }

    updateEfnCase(updatedCase: EfnCase): Observable<EfnCase> {
        const request = this._efnApiService.updateEfnCase(updatedCase).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setSelectedEfnCaseAction(result);
                this._notificationService.showSuccess('EFN Case successfully updated!');
            }, error: () => {
                this._notificationService.showError('There was a problem updating this EFN Case.');
            }
        });
        return request;
    }

    getEfnCasesByClientId(id: string): Observable<EfnCase[]> {
        const request = this._efnApiService.getEfnCasesByClient(id).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setClientsEfnCasesAction(result);
            }, error: () => {
                this._notificationService.showError('There was an error getting the Client\'s Cases.');
            }
        });
        return request;
    }

    getEfnCaseById(id: string): Observable<EfnCase> {
        const request = this._efnApiService.getEfnCase(id).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setSelectedEfnCaseAction(result);
            }, error: () => {
                this._notificationService.showError('There was an error getting the client\'s case.');
            }
        });
        return request;
    }


    getEfnCasesPaged(searchValue: string,
                     agencyId: string,
                     status: EfnCaseStatusType,
                     sortName = '',
                     limit = 100,
                     offset = 0,
                     descending = false): Observable<PagedListOfEfnCase> {
        const request = this._efnApiService.getEfnCasesPaged(
            searchValue,
            agencyId,
            status,
            limit,
            offset,
            sortName,
            null,
            descending).pipe(share());
        request.subscribe({
            error: () => {
                this._notificationService.showError('There was a problem retrieving the EFN Cases');
            }
        });
        return request;
    }

    getOpenEfnCasesByAgencyId(agencyId: string): Observable<EfnCase[]> {
        const request = this._efnApiService.getOpenEfnCasesByAgencyId(agencyId).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setOpenEfnCasesAction(result);
            }, error: () => {
                this._notificationService.showError('There was an error getting the open EFN Cases.');
            }
        });
        return request;
    }

    getFundedEfnCasesByAgencyId(agencyId: string): Observable<EfnCase[]> {
        const request = this._efnApiService.getFundedEfnCasesByAgencyId(agencyId).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setFundedEfnCasesAction(result);
            }, error: () => {
                this._notificationService.showError('There was an error getting the funded EFN Cases.');
            }
        });
        return request;
    }

    getAgencyEfnCasesByEfnCaseId(efnCaseId: string): Observable<AgencyEfnCase[]> {
        const request = this._efnApiService.getAgencyEfnCasesByEfnCaseId(efnCaseId).pipe(share());
        request.subscribe({
            next: (result) => {
                this.setAgencyEfnCasesAction(result);
            }, error: () => {
                this._notificationService.showError('Error getting efn agencies.');
            }
        });
        return request;
    }

    inviteAgencies(agencyId: string, efnCaseId: string): Observable<void> {
        const inviteAgencyParams = InviteAgencyParams.fromJS({
            agencyId,
            efnCaseId
        });
        const request = this._efnApiService.inviteAgency(inviteAgencyParams).pipe(share());
        request.subscribe({
            next: () => {
            }, error: (err) => {
                this._notificationService.showError(err || 'Error submitting invite.');
            }
        });
        return request;
    }

    getEfnCommentsByEfnCaseId(id: string): Observable<EfnCaseComment[]> {
        const request = this._efnCommentApiService.getEfnCommentsByEfnCaseId(id).pipe(share());
        request.subscribe({
            next: () => {
            }, error: (err) => {
                this._notificationService.showError(err || 'Error getting EFN comments.');
            }
        });
        return request;
    }

    addEfnCaseComment(newCaseComment: EfnCaseComment): Observable<void> {
        const request = this._efnCommentApiService.addEfnCaseComment(newCaseComment).pipe(share());
        request.subscribe({
            next: () => {
            }, error: (err) => {
                this._notificationService.showError(err || 'Error getting EFN comments.');
            }
        });
        return request;
    }

    removeAgencyEfnCase(agencyEfnCaseId: string): Observable<void> {
        const request = this._efnApiService.removeAgencyEfn(agencyEfnCaseId).pipe(share());
        request.subscribe({
            next: () => {

            }, error: err => {
                this._notificationService.showError(err || 'There was a problem removing this agency.');
            }
        });
        return request;
    }

    commitEfnCase(commitParameters: CommitEfnCaseParameters): Observable<AgencyEfnCase> {
        return this._efnApiService.commitEfnCase(commitParameters).pipe(share());
    }

    declineEfnCase({efnCaseId, agencyId}: { efnCaseId: string, agencyId: string }): Observable<void> {
        const requestBody = DeclineEfnCaseParameters.fromJS({
           efnCaseId,
           agencyId
        });

        return this._efnApiService.declineEfnCase(requestBody);
    }

    getFilesByEfnCaseId(efnCaseId: string): Observable<EfnCaseFile[]> {
        return this._efnFileApiService.getEfnFileByEfnCaseId(efnCaseId);
    }

    checkIsCaseCountyActive(county: string): Observable<boolean> {
        return this._efnApiService.isCaseCountyActive(county);
    }

    setSelectedEfnCase(efnCase: EfnCase): void {
        this.setSelectedEfnCaseAction(efnCase);
    }

    // -------- state transitions (former efn actions) ----------
    private setSelectedEfnCaseAction(selectedCase: EfnCase): void {
        this.patch({selectedEfnCase: selectedCase});
    }

    private setClientsEfnCasesAction(clientsCases: EfnCase[]): void {
        this.patch({clientsEfnCases: clientsCases});
    }

    private setOpenEfnCasesAction(openCases: EfnCase[]): void {
        this.patch({openEfnCases: openCases});
    }

    private setFundedEfnCasesAction(fundedCases: EfnCase[]): void {
        this.patch({fundedEfnCases: fundedCases});
    }

    private setAgencyEfnCasesAction(result: AgencyEfnCase[]): void {
        this.patch({agencyEfnCases: result});
    }

    // ---------------- State plumbing ----------------
    private select<T>(project: (state: EfnSliceState) => T): Observable<T> {
        return this._state$.pipe(map(project), distinctUntilChanged());
    }

    private patch(partial: Partial<EfnSliceState>): void {
        this._state$.next({...this._state$.value, ...partial});
    }
}
